import { HTMLAttributes, ReactNode, forwardRef, ForwardedRef } from "react";
import { cx } from "../../utils/cx";
import { HeaderActions } from "../HeaderActions";
import styles from "./Panel.module.scss";

export type PanelVariant = "default" | "muted" | "flush";

export type PanelDensity = "comfortable" | "compact";

export type PanelProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
    /**
     * `default` = white surface + grey stroke + tiny shadow.
     * `muted` = sunken fill for nested groups.
     * `flush` = no border/radius (embed inside another surface).
     */
    variant?: PanelVariant;
    /** Outer padding and vertical rhythm (`comfortable` = 24px per Figma). */
    density?: PanelDensity;
    /**
     * Optional chrome header. When `header` is set, it replaces the default
     * title + description + actions row entirely.
     */
    header?: ReactNode;
    /** Panel title (Figma: 17px / regular / primary). */
    title?: ReactNode;
    /**
     * Supporting copy under the title (Figma `8467:41289`, 14px secondary).
     */
    description?: ReactNode;
    /** Trailing controls in the header row. */
    headerActions?: ReactNode;
    /**
     * When true, draws a stroke under the header row (optional chrome; the
     * doc frame `8467:41287` uses open vertical rhythm instead).
     */
    showHeaderDivider?: boolean;
    children: ReactNode;
    /** Optional footer strip (e.g. secondary actions). */
    footer?: ReactNode;
};

export type PanelStackProps = HTMLAttributes<HTMLDivElement>;

export type PanelGroupProps = HTMLAttributes<HTMLDivElement>;

const PanelStack = forwardRef(function PanelStack(
    { className, ...rest }: PanelStackProps,
    ref: ForwardedRef<HTMLDivElement>,
) {
    return (
        <div
            className={cx(styles.stack, className)}
            data-ui="panel-stack"
            ref={ref}
            {...rest}
        />
    );
});

PanelStack.displayName = "Panel.Stack";

const PanelGroup = forwardRef(function PanelGroup(
    { className, ...rest }: PanelGroupProps,
    ref: ForwardedRef<HTMLDivElement>,
) {
    return (
        <div
            className={cx(styles.group, className)}
            data-ui="panel-group"
            ref={ref}
            {...rest}
        />
    );
});

PanelGroup.displayName = "Panel.Group";

/**
 * Treez DS — Panel
 *
 * Section container for create/edit flows (Figma `8467:41286`). Use
 * `Panel.Stack` for 24px between content areas and `Panel.Group` for 12px
 * between fields inside an area.
 */
function PanelRoot({
    variant = "default",
    density = "comfortable",
    header,
    title,
    description,
    headerActions,
    showHeaderDivider = false,
    children,
    footer,
    className,
    ...rest
}: PanelProps) {
    const showDefaultHeader =
        header == null &&
        (title != null || description != null || headerActions != null);
    const showHeaderRow = header != null || showDefaultHeader;

    return (
        <div
            className={cx(styles.root, className)}
            data-ui="panel"
            data-variant={variant}
            {...rest}
        >
            <div className={styles.inner} data-density={density}>
                {showHeaderRow ? (
                    <div
                        className={styles.header}
                        data-divider={showHeaderDivider ? "true" : "false"}
                    >
                        {header != null ? (
                            header
                        ) : (
                            <>
                                {title != null || description != null ? (
                                    <div className={styles.headerText}>
                                        {title != null ? (
                                            <h2 className={styles.title}>
                                                {title}
                                            </h2>
                                        ) : null}
                                        {description != null ? (
                                            <p className={styles.description}>
                                                {description}
                                            </p>
                                        ) : null}
                                    </div>
                                ) : null}
                                {headerActions ? (
                                    <div className={styles.actions}>
                                        {headerActions}
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                ) : null}
                <div className={styles.body}>{children}</div>
                {footer != null ? (
                    <div className={styles.footer}>{footer}</div>
                ) : null}
            </div>
        </div>
    );
}

PanelRoot.displayName = "Panel";

export const Panel = Object.assign(PanelRoot, {
    Stack: PanelStack,
    Group: PanelGroup,
    /** Responsive header buttons → mobile overflow menu. */
    HeaderActions,
});
